import { useState, useRef } from "react";
import type { User } from "@supabase/supabase-js";
import type { Tenant } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Shield, Upload, CheckCircle2, Loader2, Camera, CreditCard, Eye, EyeOff } from "lucide-react";

interface Props {
  tenant: Tenant;
  user: User;
  onComplete: () => void;
}

const UserKyc = ({ tenant, user, onComplete }: Props) => {
  const { toast } = useToast();
  const [step, setStep] = useState(0); // 0=intro, 1=ssn+dob, 2=licence, 3=done
  const [ssn, setSsn] = useState("");
  const [showSsn, setShowSsn] = useState(false);
  const [dob, setDob] = useState("");
  const [licenceFront, setLicenceFront] = useState<File | null>(null);
  const [licenceBack, setLicenceBack] = useState<File | null>(null);
  const [licenceFrontPreview, setLicenceFrontPreview] = useState<string | null>(null);
  const [licenceBackPreview, setLicenceBackPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (side: "front" | "back", file: File) => {
    const url = URL.createObjectURL(file);
    if (side === "front") {
      setLicenceFront(file);
      setLicenceFrontPreview(url);
    } else {
      setLicenceBack(file);
      setLicenceBackPreview(url);
    }
  };

  const handleSubmit = async () => {
    if (!ssn || ssn.length < 4 || !dob || !licenceFront || !licenceBack) {
      toast({ title: "Please complete all fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // Upload licence photos
      const frontPath = `${user.id}/licence-front-${Date.now()}.jpg`;
      const backPath = `${user.id}/licence-back-${Date.now()}.jpg`;

      const [frontUpload, backUpload] = await Promise.all([
        supabase.storage.from("kyc-documents").upload(frontPath, licenceFront, { contentType: licenceFront.type }),
        supabase.storage.from("kyc-documents").upload(backPath, licenceBack, { contentType: licenceBack.type }),
      ]);

      if (frontUpload.error || backUpload.error) {
        toast({ title: "Upload failed", description: "Please try again.", variant: "destructive" });
        setSubmitting(false);
        return;
      }

      // Update profile with KYC data
      const { error } = await supabase
        .from("profiles")
        .update({
          ssn_last4: ssn.slice(-4),
          ssn_encrypted: ssn, // In production, encrypt before storing
          dob,
          licence_front_path: frontPath,
          licence_back_path: backPath,
          kyc_status: "submitted",
          kyc_submitted_at: new Date().toISOString(),
        } as any)
        .eq("user_id", user.id);

      if (error) {
        toast({ title: "Save failed", description: error.message, variant: "destructive" });
      } else {
        setStep(3);
        toast({ title: "KYC Submitted! ✓", description: "We'll review your documents shortly." });
      }
    } catch (err) {
      console.error("KYC error:", err);
      toast({ title: "Something went wrong", variant: "destructive" });
    }
    setSubmitting(false);
  };

  if (step === 0) {
    return (
      <div className="p-6 max-w-lg mx-auto flex flex-col items-center gap-6 text-center animate-fade-in">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: `hsl(${tenant.primaryColor} / 0.1)` }}
        >
          <Shield className="h-8 w-8" style={{ color: `hsl(${tenant.primaryColor})` }} />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-bold font-display">Verify Your Identity</h2>
          <p className="text-sm text-muted-foreground max-w-xs">
            To issue your prepaid card, we need to verify your identity. This is a one-time process required by financial regulations.
          </p>
        </div>
        <div className="w-full space-y-3 text-left">
          {[
            { icon: CreditCard, label: "Social Security Number (SSN)" },
            { icon: Camera, label: "Photo of your driving licence (front & back)" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 rounded-xl border p-3">
              <item.icon className="h-5 w-5 text-muted-foreground shrink-0" />
              <span className="text-sm">{item.label}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Your data is encrypted and stored securely. We never share your information with third parties.
        </p>
        <Button
          size="lg"
          className="w-full font-semibold"
          style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          onClick={() => setStep(1)}
        >
          Start Verification
        </Button>
        <Button variant="ghost" size="sm" onClick={onComplete}>
          I'll do this later
        </Button>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="p-4 max-w-lg mx-auto space-y-4 animate-fade-in">
        <h2 className="text-xl font-bold font-display">Personal Details</h2>
        <Card className="shadow-card">
          <CardContent className="p-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Social Security Number (SSN)</Label>
              <div className="relative">
                <Input
                  type={showSsn ? "text" : "password"}
                  value={ssn}
                  onChange={(e) => setSsn(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="•••-••-••••"
                  maxLength={9}
                  inputMode="numeric"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowSsn(!showSsn)}
                >
                  {showSsn ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground">9-digit SSN, no dashes</p>
            </div>
            <div className="space-y-1.5">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
          </CardContent>
        </Card>
        <Button
          className="w-full font-semibold"
          disabled={ssn.length < 9 || !dob}
          style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          onClick={() => setStep(2)}
        >
          Continue
        </Button>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="p-4 max-w-lg mx-auto space-y-4 animate-fade-in">
        <h2 className="text-xl font-bold font-display">Driving Licence</h2>
        <p className="text-sm text-muted-foreground">Take or upload clear photos of the front and back of your driving licence.</p>

        {/* Front */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Front of Licence</CardTitle>
          </CardHeader>
          <CardContent>
            <input
              ref={frontRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFileSelect("front", e.target.files[0])}
            />
            {licenceFrontPreview ? (
              <div className="relative">
                <img src={licenceFrontPreview} alt="Front" className="w-full rounded-lg border" />
                <Button variant="outline" size="sm" className="absolute top-2 right-2" onClick={() => frontRef.current?.click()}>
                  Retake
                </Button>
              </div>
            ) : (
              <button
                className="w-full aspect-[3/2] rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors"
                onClick={() => frontRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tap to upload or take photo</span>
              </button>
            )}
          </CardContent>
        </Card>

        {/* Back */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Back of Licence</CardTitle>
          </CardHeader>
          <CardContent>
            <input
              ref={backRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFileSelect("back", e.target.files[0])}
            />
            {licenceBackPreview ? (
              <div className="relative">
                <img src={licenceBackPreview} alt="Back" className="w-full rounded-lg border" />
                <Button variant="outline" size="sm" className="absolute top-2 right-2" onClick={() => backRef.current?.click()}>
                  Retake
                </Button>
              </div>
            ) : (
              <button
                className="w-full aspect-[3/2] rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors"
                onClick={() => backRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tap to upload or take photo</span>
              </button>
            )}
          </CardContent>
        </Card>

        <Button
          className="w-full font-semibold"
          disabled={!licenceFront || !licenceBack || submitting}
          style={{ backgroundColor: `hsl(${tenant.primaryColor})` }}
          onClick={handleSubmit}
        >
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Submit Verification"}
        </Button>
      </div>
    );
  }

  // Step 3: Done
  return (
    <div className="p-6 max-w-lg mx-auto flex flex-col items-center gap-6 text-center animate-fade-in">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
        <CheckCircle2 className="h-8 w-8 text-success" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-bold font-display">Verification Submitted!</h2>
        <p className="text-sm text-muted-foreground">
          We're reviewing your documents. You'll be notified once your identity is verified and your card is ready to ship.
        </p>
      </div>
      <Button variant="outline" onClick={onComplete}>
        Back to Home
      </Button>
    </div>
  );
};

export default UserKyc;
